import { useEffect, useRef, useCallback } from 'react';

export const INACTIVITY_TIMEOUT_MS = 10 * 60 * 1000; // 10 minutes d'inactivité max
const THROTTLE_MS = 1000; // Throttle pour limiter l'exécution lors des mouvements de souris

export const clearSessionStorage = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('typeUser');
    localStorage.removeItem('userId');
    localStorage.removeItem('permissions');
    localStorage.removeItem('sessionCreatedAt');
    localStorage.removeItem('sessionExpiresAt');
    localStorage.removeItem('lastActivityAt');
};

export const broadcastLogout = (reason = 'expired') => {
    try {
        localStorage.setItem('sastra_logout_event', JSON.stringify({ timestamp: Date.now(), reason }));
        if (typeof BroadcastChannel !== 'undefined') {
            const channel = new BroadcastChannel('sastra_session_channel');
            channel.postMessage({ type: 'LOGOUT', reason });
            channel.close();
        }
    } catch (err) {
        console.error('[useSessionManager] Erreur broadcast logout:', err);
    }
};

/**
 * Hook React gérant l'inactivité (10 min) et la synchro inter-onglets.
 * @param {boolean} isAuthenticated - État d'authentification
 * @param {Function} onLogout - Callback de déconnexion globale
 */
export const useSessionManager = (isAuthenticated, onLogout) => {
    const lastActivityRef = useRef(Date.now());
    const lastThrottleRef = useRef(0);
    const checkIntervalRef = useRef(null);

    // Fonction d'invalidation / expiration de session
    const handleSessionExpire = useCallback(async (reasonMessage) => {
        const token = localStorage.getItem('token');
        const refreshToken = localStorage.getItem('refreshToken');

        // Notification au backend pour enregistrer la clôture exacte de la session (logoutAt = now, duration, status = closed)
        if (token) {
            try {
                const RAW_API_URL = import.meta.env.VITE_API_URL;
                const NORMALIZED_API_URL = RAW_API_URL.replace(/\/+$/, '');
                const API_BASE_URL = NORMALIZED_API_URL.endsWith('/api') ? NORMALIZED_API_URL : `${NORMALIZED_API_URL}/api`;

                await fetch(`${API_BASE_URL}/auth/logout`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({ refreshToken })
                });
            } catch (err) {
                console.error('[SessionManager] Erreur notification clôture session au backend:', err);
            }
        }

        clearSessionStorage();
        sessionStorage.setItem('session_expired_alert', 'true');
        if (reasonMessage) {
            sessionStorage.setItem('session_expired_message', reasonMessage);
        }
        broadcastLogout(reasonMessage);
        if (onLogout) {
            onLogout();
        }
    }, [onLogout]);

    // Listener d'activité utilisateur (throttled)
    const handleUserActivity = useCallback(() => {
        const now = Date.now();
        if (now - lastThrottleRef.current < THROTTLE_MS) return;
        lastThrottleRef.current = now;

        lastActivityRef.current = now;
        localStorage.setItem('lastActivityAt', now.toString());
    }, []);

    useEffect(() => {
        if (!isAuthenticated) return;

        const now = Date.now();

        // Actualiser l'horodatage d'activité lors du chargement / rafraîchissement de page
        localStorage.setItem('lastActivityAt', now.toString());
        lastActivityRef.current = now;

        // 2. Liste complète des événements d'interaction utilisateur
        const activityEvents = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'click', 'focus'];
        activityEvents.forEach((evt) => {
            window.addEventListener(evt, handleUserActivity, { passive: true });
        });

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                handleUserActivity();
            }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);

        // 3. Vérification périodique de l'inactivité (toutes les 3 secondes)
        checkIntervalRef.current = window.setInterval(() => {
            const currentNow = Date.now();
            const storedLastActivity = parseInt(localStorage.getItem('lastActivityAt') || currentNow.toString(), 10);
            const effectiveLastActivity = Math.max(lastActivityRef.current, storedLastActivity);

            // Vérifier l'inactivité réelle de 10 minutes
            if (currentNow - effectiveLastActivity >= INACTIVITY_TIMEOUT_MS) {
                console.warn('[SessionManager] Expiration après 10 minutes d\'inactivité réelle.');
                handleSessionExpire('Votre session a expiré après 10 minutes d\'inactivité. Veuillez vous reconnecter.');
                return;
            }
        }, 3000);

        // 4. Synchronisation multi-onglets via BroadcastChannel & localStorage events
        let channel = null;
        if (typeof BroadcastChannel !== 'undefined') {
            channel = new BroadcastChannel('sastra_session_channel');
            channel.onmessage = (event) => {
                if (event.data && event.data.type === 'LOGOUT') {
                    console.warn('[SessionManager] Signal de déconnexion reçu depuis un autre onglet.');
                    clearSessionStorage();
                    sessionStorage.setItem('session_expired_alert', 'true');
                    if (onLogout) onLogout();
                }
            };
        }

        const handleStorageChange = (e) => {
            if (e.key === 'sastra_logout_event' || (e.key === 'token' && !e.newValue)) {
                console.warn('[SessionManager] Suppression du token détectée.');
                clearSessionStorage();
                sessionStorage.setItem('session_expired_alert', 'true');
                if (onLogout) onLogout();
            }
        };
        window.addEventListener('storage', handleStorageChange);

        // Nettoyage au démontage
        return () => {
            activityEvents.forEach((evt) => {
                window.removeEventListener(evt, handleUserActivity);
            });
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            if (checkIntervalRef.current) {
                window.clearInterval(checkIntervalRef.current);
            }
            if (channel) {
                channel.close();
            }
            window.removeEventListener('storage', handleStorageChange);
        };
    }, [isAuthenticated, handleUserActivity, handleSessionExpire, onLogout]);
};
